export const PLAN_CHECKOUT_PATHS = new Set(['/checkout/starter-plan', '/checkout/light-plan']);

export const CHECKOUT_PURCHASE_PATHS = new Set(['/checkout', ...PLAN_CHECKOUT_PATHS]);

export const CHECKOUT_OVERLAY_PATHS = new Set([...CHECKOUT_PURCHASE_PATHS, '/checkout/account']);

const CHECKOUT_OVERLAY_FORWARD_QUERY_KEYS = [
  'checkoutTheme',
  'testVariant',
  'billingPeriod',
  'name',
  'email',
  'phone',
  'coupon',
  'variant',
];

export const isPlanCheckoutPath = (pathname = '') => PLAN_CHECKOUT_PATHS.has(pathname);

export const isCheckoutPurchasePath = (pathname = '') => CHECKOUT_PURCHASE_PATHS.has(pathname);

export const isCheckoutOverlayPath = (pathname = '') => CHECKOUT_OVERLAY_PATHS.has(pathname);

export const locationToPath = (location) => {
  if (!location || typeof location.pathname !== 'string') {
    return '/';
  }

  return `${location.pathname}${location.search || ''}${location.hash || ''}`;
};

const normalizeBackgroundLocation = (location) => {
  if (!location || typeof location.pathname !== 'string') {
    return null;
  }

  return {
    pathname: location.pathname || '/',
    search: location.search || '',
    hash: location.hash || '',
    state: location.state || null,
    key: location.key || 'checkout-background',
  };
};

export const createCheckoutOverlayState = (backgroundLocation) => {
  const normalizedBackground = normalizeBackgroundLocation(backgroundLocation);
  if (!normalizedBackground || isCheckoutOverlayPath(normalizedBackground.pathname)) {
    return {};
  }

  return {
    checkoutOverlay: true,
    checkoutOverlayBackground: normalizedBackground,
  };
};

export const getCheckoutOverlayBackground = (state) => {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const background = state.checkoutOverlayBackground;
  if (!background || typeof background.pathname !== 'string') {
    return null;
  }

  if (isCheckoutOverlayPath(background.pathname)) {
    return null;
  }

  return normalizeBackgroundLocation(background);
};

const getDestinationPathname = (to) => {
  if (typeof to === 'string') {
    try {
      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://botspot.trade';
      return new URL(to, origin).pathname;
    } catch {
      return '';
    }
  }

  if (to && typeof to === 'object' && typeof to.pathname === 'string') {
    return to.pathname;
  }

  return '';
};

const getSearchParams = (search = '') => new URLSearchParams(search || '');

const mergeCheckoutQueryParams = (destinationSearch, backgroundSearch) => {
  const backgroundParams = getSearchParams(backgroundSearch);
  if ([...backgroundParams.keys()].length === 0) {
    return destinationSearch || '';
  }

  const destinationParams = getSearchParams(destinationSearch);
  let changed = false;

  for (const key of CHECKOUT_OVERLAY_FORWARD_QUERY_KEYS) {
    if (!backgroundParams.has(key) || destinationParams.has(key)) {
      continue;
    }

    destinationParams.set(key, backgroundParams.get(key));
    changed = true;
  }

  if (!changed) {
    return destinationSearch || '';
  }

  const nextSearch = destinationParams.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const withForwardedCheckoutQueryParams = (to, backgroundLocation) => {
  if (!backgroundLocation?.search || !isCheckoutOverlayPath(getDestinationPathname(to))) {
    return to;
  }

  if (typeof to === 'string') {
    try {
      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://botspot.trade';
      const parsed = new URL(to, origin);
      const nextSearch = mergeCheckoutQueryParams(parsed.search, backgroundLocation.search);
      if (nextSearch === parsed.search) {
        return to;
      }

      return `${parsed.pathname}${nextSearch}${parsed.hash}`;
    } catch {
      return to;
    }
  }

  if (to && typeof to === 'object') {
    const nextSearch = mergeCheckoutQueryParams(to.search || '', backgroundLocation.search);
    if (nextSearch === (to.search || '')) {
      return to;
    }

    return {
      ...to,
      search: nextSearch,
    };
  }

  return to;
};

export const getCheckoutOverlayNavigationOptions = (to, backgroundLocation, options = {}) => {
  if (!isCheckoutOverlayPath(getDestinationPathname(to))) {
    return options;
  }

  const overlayState = createCheckoutOverlayState(backgroundLocation);
  if (!overlayState.checkoutOverlayBackground) {
    return options;
  }

  return {
    ...options,
    state: {
      ...(options.state || {}),
      ...overlayState,
    },
  };
};

export const navigateToCheckoutOverlay = (navigate, to, backgroundLocation, options = {}) => {
  const destination = withForwardedCheckoutQueryParams(to, backgroundLocation);
  navigate(
    destination,
    getCheckoutOverlayNavigationOptions(destination, backgroundLocation, options),
  );
};
